/**
 * Provider rows older than this are labelled "Archived": they describe what a
 * provider published at collection time, not necessarily what it offers now.
 */
export const PROVIDER_ARCHIVED_AFTER_MS = 7 * 24 * 60 * 60 * 1000;
export const PROVIDER_ARCHIVED_AFTER_DAYS = 7;
function validDate(value) {
    return typeof value === 'string' && !Number.isNaN(Date.parse(value));
}
export function catalogGeneratedAt(snapshot) {
    const value = snapshot?.metadata?.components?.catalog?.generatedAt ?? snapshot?.timestamp;
    return validDate(value) ? value : undefined;
}
export function isArchivedAt(collectedAt, now = Date.now()) {
    if (!validDate(collectedAt))
        return false;
    return now - Date.parse(collectedAt) >= PROVIDER_ARCHIVED_AFTER_MS;
}
function activeCounts(snapshot) {
    const counts = new Map();
    const offerings = snapshot.modelOfferings ?? [];
    if (offerings.length > 0) {
        for (const offering of offerings) {
            const provider = offering.deployment?.cloudProvider;
            if (!provider || offering.status === 'REMOVED')
                continue;
            counts.set(provider, (counts.get(provider) ?? 0) + 1);
        }
        return counts;
    }
    for (const profile of snapshot.profiles ?? []) {
        if (!profile.cloudProvider || profile.status === 'REMOVED')
            continue;
        counts.set(profile.cloudProvider, (counts.get(profile.cloudProvider) ?? 0) + 1);
    }
    return counts;
}
/**
 * Per-provider collection dates for a catalogue. Catalogues written by a
 * partial run carry `metadata.providerCollection`; older catalogues (a single
 * full collection) use the catalogue's own generation time for every provider.
 * Sorted newest first, then by provider name.
 */
export function getProviderFreshness(snapshot, now = Date.now()) {
    if (!snapshot)
        return [];
    const counts = activeCounts(snapshot);
    const collection = snapshot.metadata?.providerCollection ?? {};
    const fallbackAt = catalogGeneratedAt(snapshot);
    const providers = new Set([
        ...Object.keys(collection),
        ...counts.keys(),
    ]);
    const rows = [];
    for (const provider of providers) {
        const entry = collection[provider];
        const collectedAt = validDate(entry?.collectedAt) ? entry.collectedAt : fallbackAt;
        if (!collectedAt)
            continue;
        const ageMs = Math.max(0, now - Date.parse(collectedAt));
        rows.push({
            provider,
            source: entry?.source ?? 'live',
            collectedAt,
            status: isArchivedAt(collectedAt, now) ? 'archived' : 'current',
            ageDays: Math.floor(ageMs / (24 * 60 * 60 * 1000)),
            activeOfferings: entry?.activeOfferings ?? counts.get(provider) ?? 0,
        });
    }
    return rows.sort((a, b) => Date.parse(b.collectedAt) - Date.parse(a.collectedAt) || a.provider.localeCompare(b.provider));
}
export function getProviderFreshnessFor(snapshot, provider, now = Date.now()) {
    if (!provider)
        return undefined;
    return getProviderFreshness(snapshot, now).find((row) => row.provider === provider);
}
/** The oldest provider collection time: a catalogue is only as current as this. */
export function oldestProviderCollectedAt(rows) {
    return rows.reduce((oldest, row) => (!oldest || Date.parse(row.collectedAt) < Date.parse(oldest) ? row.collectedAt : oldest), undefined);
}
//# sourceMappingURL=providerFreshness.js.map